import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { Section } from "@/components/Section";
import { supabase } from "@/integrations/supabase/client";
import {
  isAdmin,
  getPillarForEdit,
  savePillarPage,
  generatePillarFromBrief,
  uploadPostImage,
} from "@/lib/admin.functions";

export const Route = createFileRoute("/admin/pillar/$slug")({
  ssr: false,
  beforeLoad: async () => {
    const { redirect } = await import("@tanstack/react-router");
    const { data } = await supabase.auth.getSession();
    if (!data.session) throw redirect({ to: "/auth", search: {} });
  },
  head: () => ({
    meta: [
      { title: "Editar página pilar | Empresa Brasil Online" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PillarEditor,
});

type Item = {
  position?: number;
  name?: string;
  brand?: string;
  image?: string;
  image_alt?: string;
  rating?: number;
  short_desc?: string;
  best_for?: string;
  price_from?: string;
  pros?: string[];
  cons?: string[];
  features?: string[];
  affiliate_url?: string;
  affiliate_label?: string;
  marketplace?: string;
  review_url?: string;
  paragraph?: string;
};

type FormState = {
  id?: string;
  slug: string;
  title: string;
  subtitle: string;
  intro: string;
  eyebrow: string;
  meta_title: string;
  meta_description: string;
  hero_image: string;
  conclusion: string;
  items: Item[];
  published: boolean;
};

const empty: FormState = {
  slug: "",
  title: "",
  subtitle: "",
  intro: "",
  eyebrow: "",
  meta_title: "",
  meta_description: "",
  hero_image: "",
  conclusion: "",
  items: [],
  published: true,
};

function PillarEditor() {
  const { slug } = useParams({ from: "/admin/pillar/$slug" });
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>({ ...empty, slug });
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // AI generator state
  const [brief, setBrief] = useState("");
  const [summaries, setSummaries] = useState("");
  const [itemsCount, setItemsCount] = useState(6);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!data.user) { navigate({ to: "/auth", replace: true, search: {} }); return; }
      try {
        const a = await isAdmin();
        if (!a.admin) { setAllowed(false); setLoading(false); return; }
        setAllowed(true);
        const row = (await getPillarForEdit({ data: { slug } })) as any;
        if (row) {
          setForm({
            id: row.id, slug: row.slug,
            title: row.title ?? "",
            subtitle: row.subtitle ?? "",
            intro: row.intro ?? "",
            eyebrow: row.eyebrow ?? "",
            meta_title: row.meta_title ?? "",
            meta_description: row.meta_description ?? "",
            hero_image: row.hero_image ?? "",
            conclusion: row.conclusion ?? "",
            items: Array.isArray(row.items) ? (row.items as Item[]) : [],
            published: Boolean(row.published),
          });
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erro");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, navigate]);

  function updateItem(idx: number, patch: Partial<Item>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  }
  function moveItem(idx: number, dir: -1 | 1) {
    setForm((f) => {
      const items = [...f.items];
      const j = idx + dir;
      if (j < 0 || j >= items.length) return f;
      [items[idx], items[j]] = [items[j], items[idx]];
      items.forEach((it, i) => (it.position = i + 1));
      return { ...f, items };
    });
  }
  function addItem() {
    setForm((f) => ({
      ...f,
      items: [...f.items, { position: f.items.length + 1, name: "", pros: [], cons: [], features: [] }],
    }));
  }
  function removeItem(idx: number) {
    if (!confirm("Remover este produto do ranking?")) return;
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx).map((it, i) => ({ ...it, position: i + 1 })) }));
  }

  async function uploadImage(file: File, target: "hero" | { itemIdx: number }) {
    const buf = await file.arrayBuffer();
    let bin = "";
    const bytes = new Uint8Array(buf);
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    const base64 = btoa(bin);
    const r = await uploadPostImage({ data: { fileName: file.name, contentType: file.type || "image/jpeg", base64 } });
    if (target === "hero") setForm((f) => ({ ...f, hero_image: r.url }));
    else updateItem(target.itemIdx, { image: r.url });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await savePillarPage({
        data: {
          id: form.id,
          slug: form.slug,
          title: form.title,
          subtitle: form.subtitle || null,
          intro: form.intro || null,
          eyebrow: form.eyebrow || null,
          meta_title: form.meta_title || null,
          meta_description: form.meta_description || null,
          hero_image: form.hero_image || null,
          conclusion: form.conclusion || null,
          items: form.items.map((it, i) => ({ ...it, position: it.position ?? i + 1 })),
          published: form.published,
        },
      });
      navigate({ to: "/admin" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  }

  async function runGenerate() {
    if (!brief.trim()) return;
    setGenerating(true);
    setError(null);
    try {
      const r = await generatePillarFromBrief({ data: { slug: form.slug, brief, productSummaries: summaries, itemsCount } });
      setForm((f) => ({
        ...f,
        title: f.title || r.title,
        subtitle: f.subtitle || r.subtitle,
        eyebrow: f.eyebrow || r.eyebrow,
        intro: f.intro || r.intro,
        meta_title: f.meta_title || r.meta_title,
        meta_description: f.meta_description || r.meta_description,
        conclusion: f.conclusion || r.conclusion,
        items: r.items.map((it: any, i: number) => ({
          position: it.position ?? i + 1,
          name: it.name, brand: it.brand,
          short_desc: it.short_desc, best_for: it.best_for,
          rating: it.rating, price_from: it.price_from,
          pros: it.pros ?? [], cons: it.cons ?? [], features: it.features ?? [],
          paragraph: it.paragraph,
          affiliate_label: it.affiliate_label, marketplace: it.marketplace,
        })),
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao gerar");
    } finally {
      setGenerating(false);
    }
  }

  if (loading) return <Section><p className="text-sm text-muted-foreground">Carregando...</p></Section>;
  if (!allowed) return <Section><p className="text-sm text-destructive">Sem permissão.</p></Section>;

  return (
    <Section>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Editar página pilar · /{form.slug}</h1>
        <button type="button" onClick={() => navigate({ to: "/admin" })} className="text-sm text-muted-foreground hover:text-foreground">← Voltar</button>
      </div>

      <details className="card-glass mb-6" open={form.items.length === 0}>
        <summary className="cursor-pointer text-base font-semibold">✨ Gerar página automaticamente a partir de um resumo</summary>
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-sm font-medium">Briefing / ângulo do comparativo</label>
            <textarea rows={4} value={brief} onChange={(e) => setBrief(e.target.value)}
              placeholder="Ex.: Guia completo para abrir uma empresa MEI em 2026. Foco em passos práticos, obrigações fiscais, custos e diferenças entre MEI, ME e LTDA."
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium">Resumo dos produtos que devem entrar (opcional)</label>
            <textarea rows={6} value={summaries} onChange={(e) => setSummaries(e.target.value)}
              placeholder={"Ex.:\n1) Roborock Q7 Max+ — sucção 4200 Pa, esvazia sozinho, R$ 3.499\n2) Xiaomi S10+ — LDS a laser, R$ 2.199\n..."}
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs">Qtd. de produtos</label>
              <input type="number" min={3} max={10} value={itemsCount}
                onChange={(e) => setItemsCount(Math.max(3, Math.min(10, Number(e.target.value) || 6)))}
                className="mt-1 w-24 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
            </div>
            <button type="button" onClick={runGenerate} disabled={generating || !brief.trim()} className="btn-primary disabled:opacity-50">
              {generating ? "Gerando..." : "✨ Gerar conteúdo"}
            </button>
            <p className="text-xs text-muted-foreground">A geração preenche campos vazios. Você ainda revisa antes de salvar. Lembre de adicionar os links de afiliado manualmente em cada item.</p>
          </div>
        </div>
      </details>

      <form onSubmit={onSubmit} className="space-y-6">
        <div className="card-glass space-y-4">
          <h2 className="text-lg font-semibold">Conteúdo do topo</h2>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium">Etiqueta (eyebrow)</label>
              <input value={form.eyebrow} onChange={(e) => setForm((f) => ({ ...f, eyebrow: e.target.value }))}
                placeholder="Atualizado em 2025"
                className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium">Subtítulo</label>
              <input value={form.subtitle} onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Título (H1)</label>
            <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium">Introdução</label>
            <textarea rows={5} value={form.intro} onChange={(e) => setForm((f) => ({ ...f, intro: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium">Meta title (SEO)</label>
              <input value={form.meta_title} onChange={(e) => setForm((f) => ({ ...f, meta_title: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium">Meta description (SEO)</label>
              <textarea rows={2} value={form.meta_description} onChange={(e) => setForm((f) => ({ ...f, meta_description: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Imagem hero (opcional)</label>
            <input value={form.hero_image} onChange={(e) => setForm((f) => ({ ...f, hero_image: e.target.value }))}
              placeholder="https://..."
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
            <label className="mt-2 inline-block cursor-pointer rounded-lg border border-border bg-background/60 px-3 py-1.5 text-xs">
              📤 Upload da imagem hero
              <input type="file" accept="image/*" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(f, "hero"); e.currentTarget.value = ""; }} />
            </label>
            {form.hero_image && <img src={form.hero_image} alt="hero" className="mt-2 h-32 rounded-lg object-cover" />}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} />
            Publicar
          </label>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Ranking de produtos ({form.items.length})</h2>
            <button type="button" onClick={addItem} className="rounded-lg border border-primary/40 bg-primary/10 px-3 py-1.5 text-sm text-primary">+ Adicionar produto</button>
          </div>

          {form.items.map((it, idx) => (
            <div key={idx} className="card-glass space-y-3">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-semibold">#{idx + 1} · {it.name || "(sem nome)"}</h3>
                <div className="flex gap-1">
                  <button type="button" onClick={() => moveItem(idx, -1)} className="rounded border border-border px-2 py-1 text-xs">↑</button>
                  <button type="button" onClick={() => moveItem(idx, 1)} className="rounded border border-border px-2 py-1 text-xs">↓</button>
                  <button type="button" onClick={() => removeItem(idx)} className="rounded border border-destructive/40 px-2 py-1 text-xs text-destructive">Remover</button>
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <input value={it.name ?? ""} onChange={(e) => updateItem(idx, { name: e.target.value })}
                  placeholder="Nome do produto" className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
                <input value={it.brand ?? ""} onChange={(e) => updateItem(idx, { brand: e.target.value })}
                  placeholder="Marca" className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
                <input value={it.short_desc ?? ""} onChange={(e) => updateItem(idx, { short_desc: e.target.value })}
                  placeholder="Descrição curta (1-2 frases)"
                  className="md:col-span-2 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
                <input value={it.best_for ?? ""} onChange={(e) => updateItem(idx, { best_for: e.target.value })}
                  placeholder="Ideal para (perfil de usuário)"
                  className="md:col-span-2 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
                <input type="number" min={0} max={5} step="0.1"
                  value={it.rating ?? ""} onChange={(e) => updateItem(idx, { rating: e.target.value ? Number(e.target.value) : undefined })}
                  placeholder="Nota 0-5" className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
                <input value={it.price_from ?? ""} onChange={(e) => updateItem(idx, { price_from: e.target.value })}
                  placeholder="A partir de (R$)" className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
              </div>

              <div className="rounded-lg border border-border bg-background/40 p-3">
                <label className="text-xs font-semibold">Imagem</label>
                <input value={it.image ?? ""} onChange={(e) => updateItem(idx, { image: e.target.value })}
                  placeholder="URL da imagem"
                  className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
                <label className="mt-2 inline-block cursor-pointer rounded-lg border border-border bg-background/60 px-3 py-1.5 text-xs">
                  📤 Upload
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(f, { itemIdx: idx }); e.currentTarget.value = ""; }} />
                </label>
                <input value={it.image_alt ?? ""} onChange={(e) => updateItem(idx, { image_alt: e.target.value })}
                  placeholder="Alt text da imagem (acessibilidade + SEO)"
                  className="mt-2 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
                {it.image && <img src={it.image} alt={it.image_alt || it.name || "produto"} className="mt-2 h-24 w-24 rounded-lg object-cover" />}
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <input value={it.affiliate_url ?? ""} onChange={(e) => updateItem(idx, { affiliate_url: e.target.value })}
                  placeholder="Link de afiliado (https://...)"
                  className="md:col-span-2 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
                <input value={it.marketplace ?? ""} onChange={(e) => updateItem(idx, { marketplace: e.target.value })}
                  placeholder="Marketplace" className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
                <input value={it.affiliate_label ?? ""} onChange={(e) => updateItem(idx, { affiliate_label: e.target.value })}
                  placeholder="Texto do botão (CTA)" className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
                <input value={it.review_url ?? ""} onChange={(e) => updateItem(idx, { review_url: e.target.value })}
                  placeholder="URL do review completo (interno)"
                  className="md:col-span-2 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <div>
                  <label className="text-xs font-semibold">Prós (uma por linha)</label>
                  <textarea rows={4} value={(it.pros ?? []).join("\n")}
                    onChange={(e) => updateItem(idx, { pros: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                    className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold">Contras (uma por linha)</label>
                  <textarea rows={4} value={(it.cons ?? []).join("\n")}
                    onChange={(e) => updateItem(idx, { cons: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                    className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold">Características (uma por linha)</label>
                  <textarea rows={4} value={(it.features ?? []).join("\n")}
                    onChange={(e) => updateItem(idx, { features: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                    className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold">Parágrafo de análise</label>
                <textarea rows={3} value={it.paragraph ?? ""}
                  onChange={(e) => updateItem(idx, { paragraph: e.target.value })}
                  placeholder="Texto editorial explicando por que este produto está nesta posição"
                  className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
              </div>
            </div>
          ))}
        </div>

        <div className="card-glass">
          <label className="text-sm font-medium">Conclusão (rodapé)</label>
          <textarea rows={4} value={form.conclusion} onChange={(e) => setForm((f) => ({ ...f, conclusion: e.target.value }))}
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => navigate({ to: "/admin" })} className="rounded-lg border border-border px-4 py-2 text-sm">Cancelar</button>
          <button type="submit" disabled={saving} className="btn-primary">{saving ? "Salvando..." : "Salvar página pilar"}</button>
        </div>
      </form>
    </Section>
  );
}
